import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ListTodo, Plus, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState({ title: '', description: '', assigned_to: '', branch_id: '', deadline: '' });

  const isManager = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const [taskRes, empRes, brRes] = await Promise.all([
        api.get('/tasks', { params }),
        isManager ? api.get('/employees') : Promise.resolve({ data: [] }),
        api.get('/branches')
      ]);
      setTasks(taskRes.data);
      setEmployees(empRes.data);
      setBranches(brRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus, isManager]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      await api.post('/tasks', form);
      toast.success('Task created');
      setDialogOpen(false);
      setForm({ title: '', description: '', assigned_to: '', branch_id: '', deadline: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleStatus = async (task) => {
    let newStatus;
    if (task.status === 'pending') newStatus = 'in_progress';
    else if (task.status === 'in_progress') newStatus = 'completed';
    else if (task.status === 'overdue') newStatus = 'in_progress';
    else newStatus = 'pending';
    try {
      await api.put(`/tasks/${task.id}`, { status: newStatus });
      toast.success(`Task marked as ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const getEmpName = (eid) => {
    if (eid === user?.id) return 'Me';
    return employees.find(e => e.id === eid)?.name || 'Unknown';
  };

  const isOverdue = (deadline) => deadline && new Date(deadline) < new Date();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Tasks</h1>
          <p className="text-neutral-500 text-sm mt-1">{tasks.length} tasks total</p>
        </div>
        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="task-filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          {isManager && (
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="create-task-button"
              onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> New Task
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-neutral-200 rounded animate-pulse" />)}</div>
      ) : tasks.length === 0 ? (
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardContent className="py-12 text-center text-neutral-500">
            <ListTodo className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No tasks found
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tasks.map(task => (
            <Card key={task.id} className="border border-neutral-200 shadow-sm rounded-md hover:shadow-md transition-shadow duration-200"
              data-testid={`task-card-${task.id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`text-sm font-semibold ${task.status === 'completed' ? 'text-neutral-400 line-through' : 'text-neutral-900'}`}>
                        {task.title}
                      </h3>
                      <Badge className={`text-xs capitalize ${task.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {task.status}
                      </Badge>
                      {isOverdue(task.deadline) && task.status === 'pending' && (
                        <Badge className="text-xs bg-red-100 text-red-700">Overdue</Badge>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-sm text-neutral-500 mt-1 line-clamp-2">{task.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-neutral-400">
                      <span>Assigned to: <strong className="text-neutral-600">{getEmpName(task.assigned_to)}</strong></span>
                      {task.deadline && <span>Due: <strong className="text-neutral-600">{task.deadline}</strong></span>}
                      {task.assigned_by_name && <span className="hidden sm:inline">By: {task.assigned_by_name}</span>}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-sm flex-shrink-0"
                    onClick={() => toggleStatus(task)} data-testid={`toggle-task-${task.id}`}>
                    {task.status === 'pending' ? (
                      <><Clock className="h-3.5 w-3.5 mr-1.5 text-blue-500" /> Start</>
                    ) : task.status === 'in_progress' || task.status === 'overdue' ? (
                      <><CheckCircle className="h-3.5 w-3.5 mr-1.5 text-emerald-500" /> Done</>
                    ) : (
                      <><Clock className="h-3.5 w-3.5 mr-1.5 text-amber-500" /> Reopen</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="create-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Create Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Title</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="rounded-sm" data-testid="task-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Description</Label>
              <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={3} className="rounded-sm" data-testid="task-description-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Assign to</Label>
              <Select value={form.assigned_to} onValueChange={v => setForm(p => ({ ...p, assigned_to: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="task-assign-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Deadline</Label>
              <Input type="date" value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))}
                className="rounded-sm" data-testid="task-deadline-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreate} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="task-submit-button">
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
